import { Dispatch, AnyAction } from '@reduxjs/toolkit';
import { productWizardActions } from '../../../model/store';
import { Fund } from 'funds-model';
import { ProductType, PRODUCTS} from 'cart-model';

const FUND_PARAM = 'fund';
// Regex matching string with pattern "<productType>|<identifier>|<amount>"
const FUNDS_REGEX = /^(?<productType>FUNDS|IKE|IKZE)\|(?<identifier>.+)\|(?<amount>\d+(,\d{2})?)$/;

type CartFundInfo = {
  fund: Fund;
  productType: ProductType;
  amount: number;
};

function parseFundsFromSearchParams(searchParams: URLSearchParams, funds: Fund[]): CartFundInfo[] {
  return searchParams.getAll(FUND_PARAM)
      .map(fund => fund.match(FUNDS_REGEX))
      .filter(found => found && found.groups)
      .map(found => mapToCartFundPayloadType(found!.groups!, funds))
      .filter(cartFundPayload => cartFundPayload) as CartFundInfo[];
}

function mapToCartFundPayloadType(groups: { [key: string]: string }, funds: Fund[]): CartFundInfo | null {
  const productType = groups.productType as ProductType;
  const fundAfsId = groups.identifier;
  const fund = getFundIfValid(fundAfsId, productType, funds);
  const amountStr = groups.amount.replace(',', '.');
  const amount = parseFloat(amountStr);

  if (productType && fund && amount) {
    return { fund, productType, amount };
  }
  return null;
}

function getFundIfValid(fundAfsId: string, productType: ProductType, funds: Fund[]): Fund | undefined {
  const unitCategory = PRODUCTS[productType].unitCategory;
  return funds.find(f => f.afsCodeForUnitCode[unitCategory] === fundAfsId);
}

/**
 * If URL search parameters contain the parameter (or parameters) with name {@link FUND_PARAM}
 * and its value is a string with the pattern "<productType>|<identifier>|<amount>" parses the parameter
 * and initializes the current user's cart with that product and amount.
 *
 * @return {@code true} if any product was successfully parsed from parameters, otherwise {@code false}
 */
function initCartFromParameters(searchParams: URLSearchParams, funds: Fund[], blockedFundIdns: string[], dispatch: Dispatch<AnyAction>): boolean {
  const fundsToAdd = parseFundsFromSearchParams(searchParams, funds)
      .filter(fund => {
        const afsCodeForCartItem = fund.fund.afsCodeForUnitCode[PRODUCTS[fund.productType].unitCategory];
        return !blockedFundIdns.includes(afsCodeForCartItem);
      });

  if (fundsToAdd.length > 0) {
    dispatch(productWizardActions.addCartFunds(fundsToAdd));
    dispatch(productWizardActions.setProductType(fundsToAdd[0].productType));
  }
  return fundsToAdd.length > 0;
}

export { initCartFromParameters };
